<script setup lang="ts">
import device from '@@/bits/device'
import { __ } from '@@/bits/intl'
import { goBack } from '@@/bits/location'
import OzBackButton from '@@/library/v4/components/OzBackButton.vue'
import OzContainedButton, { OzContainedButtonColorScheme } from '@@/library/v4/components/OzContainedButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzInput from '@@/library/v4/components/OzInput.vue'
import OzLabelInput from '@@/library/v4/components/OzLabelInput.vue'
import OzPadletLogo from '@@/library/v4/components/OzPadletLogo.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import { useAuthOrgVerificationStore } from '@@/pinia/auth_org_verification_store'
import { useWindowSizeStore } from '@@/pinia/window_size'
import AuthActionMenu from '@@/vuecomponents/AuthActionMenu.vue'
import AuthBottomNav from '@@/vuecomponents/AuthBottomNav.vue'
import SkipToContentLink from '@@/vuecomponents/SkipToContentLink.vue'
import { computed, nextTick, onMounted, ref, watch } from 'vue'

const authOrgVerificationStore = useAuthOrgVerificationStore()
const windowSizeStore = useWindowSizeStore()
const orgDomainInput = ref<InstanceType<typeof OzInput>>()
const xActionMenu = ref(false)
const isInputFocused = ref(false)
const initialScrollY = ref(0)
const isElectronApp = device.electronApp
const isDesktop = device.desktop

const isValid = computed(() => {
  return authOrgVerificationStore.orgDomainValidationMessage.length ? 'no' : 'yes'
})

const isContinueButtonDisabled = computed(() => {
  return !authOrgVerificationStore.orgDomain.length || !!authOrgVerificationStore.orgDomainValidationMessage.length
})

function onInput(e): void {
  authOrgVerificationStore.setOrgDomain(e.target.value)

  // Reset the validation message to enable the Continue button when typing on mobile and tablet
  if (authOrgVerificationStore.orgDomainValidationMessage) authOrgVerificationStore.setOrgDomainValidationMessage('')
}

function onFocusIn(): void {
  isInputFocused.value = true
}

function onBlur(): void {
  isInputFocused.value = false
  document.removeEventListener('scroll', blurInputOnScroll)
}

function blurInputOnScroll(): void {
  // Note: the scroll event is fired when people press a key on the soft keyboard only on iPad
  // we just blur the input only if the user scrolls past a certain threshold
  if (isExceededScrollableThreshold(window.scrollY)) {
    orgDomainInput.value?.blurInput()
  }
}

function isExceededScrollableThreshold(currentWindowY: number): boolean {
  const SCROLLABLE_THRESHOLD = 40 // the height of the input field
  return (
    currentWindowY > initialScrollY.value + SCROLLABLE_THRESHOLD ||
    currentWindowY < initialScrollY.value - SCROLLABLE_THRESHOLD
  )
}

watch(
  () => windowSizeStore.visualViewportHeight,
  (newHeight, oldHeight) => {
    if (isDesktop || !isInputFocused.value) return

    initialScrollY.value = window.scrollY

    // When people focus on the input, the keyboard will pop up
    // so the visual viewport height will be narrowed down.
    // we want to hide the keyboard when people try to scroll the page
    if (newHeight < oldHeight) {
      document.addEventListener('scroll', blurInputOnScroll)
    }
  },
)

watch(
  () => authOrgVerificationStore.isAwaitingServerResponse,
  (value) => {
    const html = document.getElementsByTagName('html')[0]

    // Animate the background color while waiting for the response
    if (value) {
      html.classList.add('dot-grid-loading')
    } else {
      html.classList.remove('dot-grid-loading')
    }
  },
)

onMounted(() => {
  nextTick(() => {
    orgDomainInput.value?.focusInput()
  })
})
</script>

<template>
  <div class="relative flex flex-col items-center">
    <header
      :class="[
        'fixed flex items-center justify-between box-border w-full px-4',
        isDesktop ? 'tablet-portrait:ps-5' : 'tablet-portrait:ps-6',
        isDesktop && !windowSizeStore.isSmallerThanTabletPortrait ? 'h-14' : 'h-18',
      ]"
    >
      <SkipToContentLink />

      <OzPadletLogo
        v-if="isDesktop && !windowSizeStore.isSmallerThanTabletPortrait"
        :class="[{ invisible: isElectronApp }]"
      />
      <template v-else>
        <OzBackButton @click="goBack" />

        <OzPlainButton
          :color-scheme="OzPlainButtonColorScheme.SecondaryIcon"
          :size-preset="OzPlainButtonSizePreset.H28px"
          :aria-label="__('Open more menu dialog')"
          @click="xActionMenu = true"
        >
          <OzIcon :size="20" name="more_vertical" />
        </OzPlainButton>
      </template>
    </header>

    <main
      id="main-content"
      :class="[
        'fixed',
        'bottom-0',
        'flex',
        'flex-col',
        'box-border',
        'w-full',
        'p-4',
        'pb-6',
        'tablet-portrait:relative tablet-portrait:w-[375px] tablet-portrait:p-0 tablet-portrait:px-4 tablet-portrait:mt-32',
      ]"
      tabindex="-1"
    >
      <OzBackButton
        v-if="isDesktop && !windowSizeStore.isSmallerThanTabletPortrait"
        :disabled="authOrgVerificationStore.isAwaitingServerResponse"
        class="mb-5"
        @click="goBack"
      />

      <div class="mb-5 text-dark-text-100 dark:text-light-text-100">
        <h1 class="w-56 text-login-heading-1 font-semibold px-2 mb-1">
          {{ __('Backpack/ Briefcase log in') }}
        </h1>
        <h2 class="text-login-body px-2">
          {{ __('Enter your organization domain') }}
        </h2>
      </div>

      <form novalidate class="flex flex-col" @submit.prevent="authOrgVerificationStore.verifyOrgDomain">
        <!-- Always be LTR since URLs are always in english -->
        <OzLabelInput dir="ltr" input-suffix=".padlet.org" class="mb-4">
          <template #label>
            <label
              for="org-domain-input"
              :class="['text-body-small-posts', 'text-dark-text-100 dark:text-light-text-100', 'px-2']"
            >
              {{ __('Domain') }}
            </label>
          </template>

          <template #input>
            <!--
              Use `model-value` and `@input` here instead of `v-model`
              because the input value doesn't get updated on every keypress on android when using `v-model`
              @see https://github.com/vuejs/vue/issues/9777#issuecomment-478831263
            -->
            <OzInput
              id="org-domain-input"
              ref="orgDomainInput"
              name="domain"
              type="text"
              placeholder="hogwarts"
              class="bg-light-ui-100 dark:bg-dark-ui-100"
              :disabled="authOrgVerificationStore.isAwaitingServerResponse"
              :model-value="authOrgVerificationStore.orgDomain"
              :validation-message="authOrgVerificationStore.orgDomainValidationMessage"
              :valid="isValid"
              :required="true"
              @input="onInput"
              @focusin="onFocusIn"
              @blur="onBlur"
          /></template>
        </OzLabelInput>

        <div class="md:flex md:justify-end">
          <OzContainedButton
            :color-scheme="OzContainedButtonColorScheme.Primary"
            :disabled="authOrgVerificationStore.isAwaitingServerResponse || isContinueButtonDisabled"
            class="w-full tablet-portrait:w-auto"
            >{{ __('Continue') }}</OzContainedButton
          >
        </div>
      </form>

      <AuthActionMenu v-if="xActionMenu" @cancel="xActionMenu = false" />
    </main>

    <AuthBottomNav v-if="isDesktop" class="fixed start-6 bottom-6 hidden tablet-portrait:flex" />
  </div>
</template>

<style lang="postcss">
@import '@@/styles/4/kit';
@import '@@/styles/4/dot_grid';

* {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

html {
  @extend .dot-grid;
  @apply overflow-hidden;
  @apply bg-left-top;
  @apply h-screen;

  font-size: 16px;
}

@media screen and (orientation: landscape) {
  html {
    @apply overflow-auto;
  }
}
</style>
